import { useEffect, useRef, useState } from 'react';
import { Alert, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { useAppContext } from '@/context';
import ChatTranscript from '@/chat/components/ChatTranscript';
import MessageComposer from '@/chat/components/MessageComposer';
import { useRoomSocket } from './useRoomSocket';

export default function GroupChatRoom() {
  const { roomId = '' } = useParams<{ roomId: string }>();
  const { team } = useAppContext();
  return <RoomSession key={`${team?.id}:${roomId}`} roomId={roomId} />;
}
function RoomSession({ roomId }: { roomId: string }) {
  const { user, team } = useAppContext();
  const room = useRoomSocket(roomId, team?.id);
  const [draft, setDraft] = useState<string>('');
  const [sending, setSending] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const mounted = useRef<boolean>(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const send = async () => {
    if (sending || !draft.trim()) return;
    setSending(true);
    setError(null);
    try {
      await room.send(draft.trim());
      if (mounted.current) setDraft('');
    } catch (failure) {
      if (mounted.current) setError(failure instanceof Error ? failure.message : 'Could not confirm delivery.');
    } finally {
      if (mounted.current) setSending(false);
    }
  };
  return (
    <Stack spacing={3}>
      <Stack direction="row" useFlexGap flexWrap="wrap" gap={2} alignItems="center">
        <Button component={RouterLink} to="/group-chat/">
          Back to Rooms
        </Button>
        <Typography component="h1" variant="h4">
          {roomId === 'lobby' ? 'Lobby' : `Room: ${roomId}`}
        </Typography>
      </Stack>
      <Alert
        severity={room.connection === 'connected' ? 'success' : 'info'}
        action={
          room.connection === 'disconnected' ? (
            <Button color="inherit" onClick={room.reconnect}>
              Reconnect
            </Button>
          ) : undefined
        }
      >
        {room.connection === 'connecting' && (
          <CircularProgress size={16} aria-label="Connecting to room" sx={{ mr: 1 }} />
        )}
        {room.connection === 'connected'
          ? 'Connected'
          : room.connection === 'connecting'
            ? 'Connecting…'
            : room.connection === 'denied'
              ? 'Room access unavailable'
              : 'Disconnected'}
      </Alert>
      {(error || room.error) && <Alert severity="error">{error || room.error}</Alert>}
      <ChatTranscript
        messages={room.messages}
        timeZone={user?.timezone || undefined}
        emptyText="No messages received in this session."
      />
      <Typography variant="body2" color="text.secondary">
        Named rooms are shared within the selected team. Messages are live and are not stored after leaving or reloading.
      </Typography>
      <MessageComposer
        value={draft}
        onChange={setDraft}
        onSend={() => void send()}
        pending={sending}
        unavailable={room.connection !== 'connected'}
        helperText={room.connection === 'connected' ? undefined : 'Reconnect before sending a message.'}
      />
    </Stack>
  );
}
